import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Camera, ShieldCheck, CalendarClock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const icons = [MapPin, Camera, ShieldCheck, CalendarClock];

const SolutionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].solutionData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="services" className="py-24 md:py-32 bg-[#1a1a2e] relative overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#1e1b4b] to-[#1a1a2e] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B83F0]">
            {t.label}
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-white leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-5 text-lg text-gray-400 leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.cards.map((card, i) => {
            const Icon = icons[i];
            return (
              <div
                key={i}
                className={`p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all duration-500 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-[#5B4FE9]/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#8B83F0]" />
                </div>
                <h3 className="text-base font-bold text-white">{card.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
